import type { TFunction } from "i18next";
import { OrderPriceNegotiationStatus } from "@/biz/order";
import { hasCreatorCancellation } from "@/biz/orderCancellation";
import {
  AdsOrderStatus,
  CancelReasonEnum,
  CancelReasonInfo,
  getCancelReasonInfo,
} from "@/interface/order";
import type { CreatorCancellationOrder } from "@/interface/collaborationCancellation";

interface AdsOrderStatusLabelData extends CreatorCancellationOrder {
  ahaPicksOffer?: unknown;
  priceNegotiationStatus?: OrderPriceNegotiationStatus;
  cancelReason?: CancelReasonEnum;
}

const baseOrderStatusCopy: Record<
  string,
  { key: string; defaultValue: string }
> = {
  Pending_production_kickoff: {
    key: "components.orderStatusComponent.pendingProductionKickoff",
    defaultValue: "Pending production kickoff",
  },
  Pending_creator_address: {
    key: "components.orderStatusComponent.pendingCreatorAddress",
    defaultValue: "Pending creator address",
  },
  Pending_Product_shipment: {
    key: "components.orderStatusComponent.pendingProductShipment",
    defaultValue: "Pending product shipment",
  },
  Pending_Product_Arrival: {
    key: "components.orderStatusComponent.pendingProductArrival",
    defaultValue: "Pending product arrival",
  },
  Under_script_production: {
    key: "components.orderStatusComponent.underScriptProduction",
    defaultValue: "Under script production",
  },
  Under_script_review: {
    key: "components.orderStatusComponent.underScriptReview",
    defaultValue: "Under script review",
  },
  Under_content_production: {
    key: "components.orderStatusComponent.underContentProduction",
    defaultValue: "Under content production",
  },
  Under_content_review: {
    key: "components.orderStatusComponent.underContentReview",
    defaultValue: "Under content review",
  },
  Ads_pending_publish: {
    key: "components.orderStatusComponent.adsPendingPublish",
    defaultValue: "Ads pending publish",
  },
  Ads_published: {
    key: "components.orderStatusComponent.adsPublished",
    defaultValue: "Ads published",
  },
  Pending_creator_account_info: {
    key: "components.orderStatusComponent.pendingCreatorAccountInfo",
    defaultValue: "Awaiting influencer access information",
  },
  Pending_account_setup: {
    key: "components.orderStatusComponent.pendingAccountSetup",
    defaultValue: "Pending brand setup",
  },
};

export const getBaseOrderStatusLabel = (
  status: string,
  t: TFunction,
): string | undefined => {
  const copy = baseOrderStatusCopy[status];
  return copy
    ? t(copy.key, {
        defaultValue: copy.defaultValue,
      })
    : undefined;
};

export const getAdsOrderStatusLabel = (
  status: AdsOrderStatus,
  data: AdsOrderStatusLabelData,
  t: TFunction,
) => {
  const baseStatusLabel = getBaseOrderStatusLabel(status, t);
  if (baseStatusLabel) return baseStatusLabel;

  switch (status) {
    case AdsOrderStatus.PendingSelection:
      return t("components.orderStatusComponent.pendingSelection", {
        defaultValue: "Pending your selection",
      });
    case AdsOrderStatus.QuoteRequested:
      return data?.ahaPicksOffer
        ? t("components.orderStatusComponent.awaitingInfluencerReply", {
            defaultValue: "Awaiting influencer's reply",
          })
        : t("components.orderStatusComponent.quoteRequested", {
            defaultValue: "Awaiting influencer's quote",
          });
    case AdsOrderStatus.CollaborationEstablished:
      if (
        data?.priceNegotiationStatus ===
        OrderPriceNegotiationStatus.UnderAdsFirstReview
      ) {
        return t(
          "components.orderStatusComponent.firstRateUnderAdvertiserReview",
          { defaultValue: "Influencer first rate under review" },
        );
      }
      if (
        data?.priceNegotiationStatus ===
        OrderPriceNegotiationStatus.UnderAdsReview
      ) {
        return t(
          "components.orderStatusComponent.counterOfferUnderAdvertiserReview",
          { defaultValue: "Influencer rate under review" },
        );
      }
      if (
        data?.priceNegotiationStatus ===
        OrderPriceNegotiationStatus.UnderCreatorReview
      ) {
        return t(
          "components.orderStatusComponent.priceProposalUnderInfluencerReview",
          { defaultValue: "Offer under influencer review" },
        );
      }
      if (
        data?.priceNegotiationStatus ===
        OrderPriceNegotiationStatus.PendingAdsApproval
      ) {
        return t("components.orderStatusComponent.pendingPayment", {
          defaultValue: "Ready to pay",
        });
      }
      return t("components.orderStatusComponent.collaborationEstablished", {
        defaultValue: "New candidate",
      });
    case AdsOrderStatus.CollaborationCancelled: {
      if (hasCreatorCancellation(data)) {
        return t(
          "campaignDetail.collaborationCancellation.status.influencerEnded",
          { defaultValue: "Influencer ended the collab" },
        );
      }
      const cancelReason = data?.cancelReason as CancelReasonEnum | undefined;
      return cancelReason && CancelReasonInfo[cancelReason]
        ? getCancelReasonInfo(cancelReason, t).title
        : t("components.orderStatusComponent.collaborationCancelled", {
            defaultValue: "Collaboration cancelled",
          });
    }
    case AdsOrderStatus.CollaborationWithdrawn: {
      const cancelReason = data?.cancelReason as CancelReasonEnum | undefined;
      return cancelReason && CancelReasonInfo[cancelReason]
        ? getCancelReasonInfo(cancelReason, t).title
        : t("components.orderStatusComponent.collaborationWithdrawn", {
            defaultValue: "Collaboration withdrawn",
          });
    }
    case AdsOrderStatus.Expired:
      return t("components.orderStatusComponent.expired", {
        defaultValue: "Expired",
      });
    default:
      return String(status);
  }
};
